import React, { useRef, useState } from 'react'
import { useSize } from 'components/Charts_Assets'
import { scaleLinear } from 'd3-scale'
import { line, area } from 'd3-shape'
import './styles.scss'
import Tooltip from 'components/Charts_Tooltip'
import XTicks from 'components/Charts_XTicks'
import YTicks from 'components/Charts_YTicks'
import Value from 'components/Charts_Value'
const isMobile = window.innerWidth < 712

type AnchorType = 'start' | 'end' | 'middle'
type ChartProps = {
  data: any[]
  yDomain: [number, number]
  xDomain: [Date, Date]
  className?: string
  yTicks?: { text?: number[]; lines?: number[] }
  xTicks?: { text?: string[]; lines?: string[] }
  height: number
  unit?: string
  annotations?: { d: string; v: number; t: string }[][]
}
export const ExcessDeathRelative: React.FC<ChartProps> = ({
  data,
  height = 40,
  yDomain,
  xDomain,
  className,
  yTicks,
  xTicks,
  unit,
  annotations
}) => {
  const yFunction = (d: any) =>
    yAxisScale.range()[0] + yAxisScale.range()[1] - yAxisScale(d.v)

  const xFunction = (d: any, i: number) => {
    return xAxisScale(new Date(d.d))
  }
  const [container, size] = useSize()

  const [ttData, setTtData] = useState<
    | {
        pos: { x: number; anchor: AnchorType }
        data?: { d: string; v: number }[]
      }
    | undefined
  >()
  const createTooltip = e => {
    let xgraph = xAxisScale.invert(Math.floor(e.nativeEvent.layerX))
    let date = new Date(xgraph).toISOString().split('T')[0]

    let els = data.map(d => d.find(f => f.d === date)).filter(f => f)

    if (els.length === 0) return null

    const x = xAxisScale(new Date(date))
    let ttdata = {
      pos: {
        x: x,
        anchor: (x > size.width / 2 ? 'end' : 'start') as AnchorType
      },
      data: els
    }
    setTtData(ttdata)
  }
  const cancelTooltip = () => setTtData(undefined)

  const svg = useRef<SVGSVGElement>(null)
  //
  const width = size.width
  const gutterTop = 0
  const gutterRight = size.isMobile ? 16 : 14
  const gutterLeft = 0
  const gutterBottom = 20
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size]
  )
  const lineFunction = React.useMemo(
    () =>
      line()
        .x(xFunction)
        .y(yFunction),
    [size]
  )
  const areaFunction = React.useMemo(
    () =>
      area()
        .x(xFunction)
        .y0(
          d => yAxisScale.range()[0] + yAxisScale.range()[1] - yAxisScale(d.uG)
        )
        .y1(
          d => yAxisScale.range()[0] + yAxisScale.range()[1] - yAxisScale(d.oG)
        ),
    [size]
  )
  console.log(JSON.stringify(data))

  return (
    <div className={'ExcessDeathRelative ' + className}>
      <div
        ref={container}
        style={{ position: 'relative', width: '100%', height: height }}
      >
        <svg
          ref={svg}
          height={height}
          onTouchMove={createTooltip}
          onMouseMove={createTooltip}
          onMouseLeave={cancelTooltip}
          onTouchCancel={cancelTooltip}
        >
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <React.Fragment key={'Multiline_Area_' + i}>
                  <clipPath id={'Mask_' + i}>
                    <path d={areaFunction(d)} className={'Area _' + i} />
                  </clipPath>
                </React.Fragment>
              )
            })}
          {data.length > 0 && (
            <clipPath id={'Maasked_Rect_A'}>
              <rect
                x={xAxisScale.range()[0]}
                y={yAxisScale.range()[1]}
                width={
                  xFunction(data[0][data[0].length - 1]) - xAxisScale.range()[0]
                }
                height={yAxisScale.range()[0] - yAxisScale.range()[1]}
                fill='#f00'
              />
            </clipPath>
          )}
          {xTicks && !ttData && (
            <XTicks data={xTicks} x={xAxisScale} y={yAxisScale} />
          )}
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <React.Fragment>
                  {d.map(a => {
                    return (
                      <circle
                        className={'circles _' + i}
                        cx={xFunction(a)}
                        cy={yFunction(a)}
                        r={isMobile ? 1.5 : 2.5}
                      />
                    )
                  })}
                </React.Fragment>
              )
            })}
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <React.Fragment key={'Multiline Line _' + i}>
                  <path
                    d={lineFunction(d)}
                    className={'Line  _' + i}
                    stroke='#fff'
                    clipPath={'url(#' + 'Maasked_Rect_A' + ')'}
                  />
                </React.Fragment>
              )
            })}
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <React.Fragment key={'Multiline_Area_' + i}>
                  <path
                    d={areaFunction(d)}
                    className={'Area _' + i}
                    stroke='#fff'
                  />
                </React.Fragment>
              )
            })}{' '}
          {yTicks && <YTicks data={yTicks} x={xAxisScale} y={yAxisScale} />}
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <g>
                  {/* clipPath={'url(#' + ('Mask_' + i) + ')'}> */}
                  {d.map(a => {
                    if (a.v < a.uG || a.v > a.oG) return null

                    return (
                      <circle
                        className={'circles b _' + i}
                        cx={xFunction(a)}
                        cy={yFunction(a)}
                        r={isMobile ? 1.5 : 2.5}
                      />
                    )
                  })}
                </g>
              )
            })}
          {data.length > 0 &&
            data.map((d, i) => {
              return (
                <React.Fragment key={'Multiline Masked Line _' + i}>
                  <path
                    d={lineFunction(d)}
                    className={'Line Masked  _' + i}
                    stroke='#fff'
                    clipPath={'url(#' + ('Mask_' + i) + ')'}
                  />
                  {!ttData && (
                    <Value
                      date={i === 1 ? d[d.length - 1].d : undefined}
                      value={d[d.length - 1].v}
                      x={xAxisScale(new Date(d[d.length - 1].d))}
                      y={yAxisScale}
                      offset={0}
                      textAnchor={'start'}
                      line
                      unit={unit}
                      prefix={d[d.length - 1].v > 0 ? '+' : undefined}
                    />
                  )}
                </React.Fragment>
              )
            })}
          {!ttData && (
            <>
              <text
                x={xFunction({ d: '2020-11-22' })}
                y={yFunction({ v: 74 })}
                textAnchor='middle'
                className='annotation'
              >
                +70%
              </text>
              <text
                x={xFunction({ d: '2021-12-12' })}
                y={yFunction({ v: 39 })}
                textAnchor='middle'
                className='annotation'
              >
                +35%
              </text>
              <text
                x={xFunction({ d: '2020-04-05' })}
                y={yFunction({ v: 50 })}
                textAnchor='middle'
                className='annotation'
              >
                +46%
              </text>
              <text
                x={xFunction({ d: '2021-03-21' })}
                y={yFunction({ v: -22 })}
                textAnchor='middle'
                className='annotation'
              >
                -13%
              </text>
            </>
          )}
          {annotations &&
            !ttData &&
            annotations.map((set, i) => {
              return (
                <g className={'annotation _' + i} key={'AnnotationSet_' + i}>
                  {set.map((anno, i) => {
                    return (
                      <g
                        transform={`translate(${xAxisScale(
                          new Date(anno.d)
                        )},${yAxisScale.range()[0] - yAxisScale(anno.v)})`}
                        key={'Annotation_' + i}
                      >
                        <circle r={3} />
                        <text textAnchor='middle' y={-5}>
                          {anno.t}
                        </text>
                      </g>
                    )
                  })}
                </g>
              )
            })}
          {ttData && <Tooltip data={ttData} y={yAxisScale} />}
        </svg>
      </div>
    </div>
  )
}
